import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { LucideIcon } from 'lucide-react';
import {
  Boxes, Package, Sprout, Tags, FlaskConical, Palette, ClipboardList, MapPinned, ClipboardCheck,
  TrendingUp, Paintbrush, Warehouse, Map as MapIcon, ChevronRight, Scissors, TreeDeciduous,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';

/**
 * /nursery/stock — everything about the stock itself, which was eleven cards
 * on the nursery dashboard. Two groups: what we grow and sell (tree types,
 * stock items, products…) and stock control (lots, locations, farms,
 * stocktakes…). Each group is sorted by its label in the reader's language.
 *
 * Label and detail of each are `tools.<key>.label` / `tools.<key>.detail`.
 */

interface Tool { key: string; icon: LucideIcon; path: string }

const GROUPS: { key: string; tools: Tool[] }[] = [
  {
    key: 'catalogue',
    tools: [
      { key: 'colourCodes', icon: Palette, path: '/nursery/colour-codes' },
      { key: 'material', icon: Scissors, path: '/nursery/material' },
      { key: 'mothers', icon: TreeDeciduous, path: '/nursery/mothers' },
      { key: 'products', icon: Tags, path: '/nursery/products' },
      { key: 'propagation', icon: FlaskConical, path: '/nursery/propagation/setup' },
      { key: 'stockItems', icon: Package, path: '/nursery/stock-items' },
      { key: 'treeTypes', icon: Sprout, path: '/nursery/tree-types' },
    ],
  },
  {
    key: 'control',
    tools: [
      { key: 'forecast', icon: TrendingUp, path: '/nursery/forecast' },
      { key: 'farms', icon: MapIcon, path: '/orchard' },
      { key: 'inventory', icon: Warehouse, path: '/nursery/inventory' },
      { key: 'locations', icon: MapPinned, path: '/nursery/locations' },
      { key: 'lots', icon: Boxes, path: '/nursery/lots' },
      { key: 'painting', icon: Paintbrush, path: '/nursery/painting' },
      { key: 'production', icon: ClipboardList, path: '/nursery/production' },
      { key: 'stocktake', icon: ClipboardCheck, path: '/nursery/stocktake' },
    ],
  },
];

function StockHub() {
  const { t, i18n } = useTranslation('nursery', { keyPrefix: 'StockHub' });
  const label = (tool: Tool) => t(`tools.${tool.key}.label`);

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Boxes className="h-6 w-6 text-white" />}
      />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {GROUPS.map((group) => (
          <section key={group.key}>
            <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide mb-2">
              {t(`groups.${group.key}`)}
            </h2>
            <div className="space-y-3">
              {[...group.tools].sort((a, b) => label(a).localeCompare(label(b), i18n.language)).map((tool) => {
                const Icon = tool.icon;
                return (
                  <Link key={tool.path} to={tool.path}
                    className="flex items-start gap-3 bg-white border border-neutral-200 rounded-xl p-4 hover:border-primary-300 hover:shadow-sm transition-all">
                    <Icon className="h-6 w-6 text-primary-600 flex-shrink-0 mt-0.5" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-semibold text-neutral-800">{label(tool)}</span>
                      <span className="block text-sm text-neutral-500 mt-0.5">{t(`tools.${tool.key}.detail`)}</span>
                    </span>
                    <ChevronRight className="h-5 w-5 text-neutral-400 flex-shrink-0 self-center" />
                  </Link>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </>
  );
}

export default StockHub;
